'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface VideoReview {
  id: number;
  title: string;
  customerName: string;
  service: string;
  thumbnail: string;
  alt: string;
  duration: string;
  views: number;
}

const VideoReviews = () => {
  const [selectedVideo, setSelectedVideo] = useState<number | null>(null);

  const videoReviews: VideoReview[] = [
    {
      id: 1,
      title: 'Villa Move Experience in Farwaniya',
      customerName: 'Ahmed Al-Rashid',
      service: 'Residential Move',
      thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_1fb2ae1fa-1765841928560.png',
      alt: 'Professional movers team loading furniture into branded Almanara moving truck',
      duration: '2:45',
      views: 1243,
    },
    {
      id: 2,
      title: 'Office Relocation Success Story',
      customerName: 'Khalid Al-Mutairi',
      service: 'Commercial Move',
      thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_17fdb6449-1768148773133.png',
      alt: 'Office relocation in progress with computers and desks being carefully moved',
      duration: '3:12',
      views: 892,
    },
    {
      id: 3,
      title: 'International Moving Made Easy',
      customerName: 'Sarah Mitchell',
      service: 'International Move',
      thumbnail: 'https://images.unsplash.com/photo-1590451669839-60ac20fba347',
      alt: 'Neatly packed moving boxes labeled and organized in modern living room',
      duration: '4:20',
      views: 756,
    },
    {
      id: 4,
      title: 'Apartment Move Review',
      customerName: 'Fatima Hassan',
      service: 'Apartment Move',
      thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_16885cae6-1767260679136.png',
      alt: 'Professional packer wrapping delicate items with protective bubble wrap',
      duration: '2:18',
      views: 634,
    },
  ];

  const handlePlay = (videoId: number) => {
    setSelectedVideo(videoId);
    console.log(`Playing video: ${videoId}`);
  };

  return (
    <section className="py-16 lg:py-24 bg-muted">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full mb-4">
            <Icon name="VideoCameraIcon" size={20} variant="solid" className="text-primary" />
            <span className="text-sm font-body font-semibold text-primary">Video Testimonials</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-text-primary mb-4">
            See What Customers Say
          </h2>
          <p className="text-lg text-text-secondary font-body">
            Watch real customers share their moving experiences with Almanara
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {videoReviews.map((video) => (
            <div
              key={video.id}
              className="bg-card rounded-xl shadow-soft hover:shadow-elevated transition-all duration-base overflow-hidden"
            >
              <div
                className="relative h-48 overflow-hidden group cursor-pointer"
                onClick={() => handlePlay(video.id)}
              >
                <AppImage
                  src={video.thumbnail}
                  alt={video.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-base"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50 transition-colors duration-base">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-base">
                    <Icon name="PlayIcon" size={32} variant="solid" className="text-primary ml-1" />
                  </div>
                </div>
                <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded text-xs font-semibold">
                  {video.duration}
                </div>
                <div className="absolute bottom-3 left-3">
                  <span className="inline-block bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-semibold">
                    {video.service}
                  </span>
                </div>
              </div>
              
              <div className="p-4">
                <h3 className="font-headline font-bold text-text-primary mb-1 line-clamp-2">
                  {video.title}
                </h3>
                <p className="text-sm text-text-secondary font-body mb-3">{video.customerName}</p>
                
                <div className="flex items-center justify-between text-xs text-text-secondary">
                  <span className="flex items-center space-x-1">
                    <Icon name="EyeIcon" size={14} variant="outline" />
                    <span>{video.views.toLocaleString()} views</span>
                  </span>
                  <button
                    onClick={() => handlePlay(video.id)}
                    className="flex items-center space-x-1 text-primary hover:text-secondary transition-colors duration-base font-semibold"
                  >
                    <span>Watch</span>
                    <Icon name="ArrowRightIcon" size={14} variant="outline" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-12">
          <a
            href="https://instagram.com/almanara_3985"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg hover:opacity-90 transition-all duration-base font-body font-bold shadow-soft"
          >
            <Icon name="CameraIcon" size={24} variant="solid" />
            <span>Watch More on Instagram</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default VideoReviews;